<template>
  <div class="booking-policy">
    <div class="policy-card">
      <el-form 
        ref="formRef" 
        :model="policyForm" 
        :rules="rules" 
        label-position="top"
        class="policy-form"
      >
      <!-- Cancellation Policy -->
      <el-form-item :label="$t('settings.cancellationPolicy')" prop="cancellationPolicy">
        <el-input 
          v-model="policyForm.cancellationPolicy" 
          type="textarea"
          :rows="3"
          :placeholder="$t('settings.cancellationPolicyPlaceholder')"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>

      <!-- Safety Rules -->
      <el-form-item :label="$t('settings.safetyRules')" prop="safetyRules">
        <el-input 
          v-model="policyForm.safetyRules" 
          type="textarea"
          :rows="3"
          :placeholder="$t('settings.safetyRulesPlaceholder')"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>

      <!-- Rider Requirements -->
      <el-form-item :label="$t('settings.riderRequirements')" prop="riderRequirements">
        <el-input 
          v-model="policyForm.riderRequirements" 
          type="textarea"
          :rows="3"
          :placeholder="$t('settings.riderRequirementsPlaceholder')"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>

      <!-- Arrival Instructions -->
      <el-form-item :label="$t('settings.arrivalInstructions')" prop="arrivalInstructions">
        <el-input 
          v-model="policyForm.arrivalInstructions" 
          type="textarea"
          :rows="2"
          :placeholder="$t('settings.arrivalInstructionsPlaceholder')"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>

      <!-- What to Bring -->
      <el-form-item :label="$t('settings.whatToBring')" prop="whatToBring">
        <el-input 
          v-model="policyForm.whatToBring" 
          type="textarea"
          :rows="2"
          :placeholder="$t('settings.whatToBringPlaceholder')"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>

      <!-- Health Disclosure -->
      <el-form-item :label="$t('settings.healthDisclosure')" prop="healthDisclosure">
        <el-input 
          v-model="policyForm.healthDisclosure" 
          type="textarea"
          :rows="2"
          :placeholder="$t('settings.healthDisclosurePlaceholder')"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>

      <!-- Weather Policy -->
      <el-form-item :label="$t('settings.weatherPolicy')" prop="weatherPolicy">
        <el-input 
          v-model="policyForm.weatherPolicy" 
          type="textarea"
          :rows="2"
          :placeholder="$t('settings.weatherPolicyPlaceholder')"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>

      <!-- Late Arrival Policy -->
      <el-form-item :label="$t('settings.lateArrivalPolicy')" prop="lateArrivalPolicy">
        <el-input 
          v-model="policyForm.lateArrivalPolicy" 
          type="textarea"
          :rows="2"
          :placeholder="$t('settings.lateArrivalPolicyPlaceholder')"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>

      <!-- Photo Release Policy -->
      <el-form-item :label="$t('settings.photoReleasePolicy')" prop="photoReleasePolicy">
        <el-input 
          v-model="policyForm.photoReleasePolicy" 
          type="textarea"
          :rows="2"
          :placeholder="$t('settings.photoReleasePolicyPlaceholder')"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>

      <!-- Terms and Conditions -->
      <el-form-item :label="$t('settings.termsAndConditions')" prop="termsAndConditions">
        <el-input 
          v-model="policyForm.termsAndConditions" 
          type="textarea"
          :rows="3"
          :placeholder="$t('settings.termsAndConditionsPlaceholder')"
          maxlength="1000"
          show-word-limit
        />
      </el-form-item>

      <!-- Liability Waiver -->
      <el-form-item :label="$t('settings.liabilityWaiver')" prop="liabilityWaiver">
        <el-input 
          v-model="policyForm.liabilityWaiver" 
          type="textarea"
          :rows="3"
          :placeholder="$t('settings.liabilityWaiverPlaceholder')"
          maxlength="1000"
          show-word-limit
        />
      </el-form-item>

      <!-- Action Buttons -->
      <el-form-item>
        <div class="action-buttons">
          <el-button 
            type="primary" 
            @click="submitForm"
            :loading="saving"
          >
            {{ $t('common.save') }}
          </el-button>
          <el-button @click="resetForm">
            {{ $t('common.cancel') }}
          </el-button>
        </div>
      </el-form-item>
    </el-form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { getBookingPolicies, updateBookingPolicies } from '@/api/settings'

const formRef = ref(null)
const saving = ref(false)
const loading = ref(true)

const defaultPolicy = {
  cancellationPolicy: '',
  safetyRules: '',
  riderRequirements: '',
  arrivalInstructions: '',
  whatToBring: '',
  healthDisclosure: '',
  weatherPolicy: '',
  lateArrivalPolicy: '',
  photoReleasePolicy: '',
  termsAndConditions: '',
  liabilityWaiver: ''
}

const policyForm = reactive({ ...defaultPolicy })

const rules = {
  cancellationPolicy: [
    { required: true, message: 'Please enter cancellation policy', trigger: 'blur' }
  ],
  safetyRules: [
    { required: true, message: 'Please enter safety rules', trigger: 'blur' }
  ],
  riderRequirements: [
    { required: true, message: 'Please enter rider requirements', trigger: 'blur' }
  ]
}

const fetchPolicies = async () => {
  loading.value = true
  try {
    const res = await getBookingPolicies()
    if (res.success && res.data) {
      Object.assign(policyForm, {
        ...defaultPolicy,
        ...res.data
      })
    }
  } catch (error) {
    console.error('Failed to fetch booking policies:', error)
    ElMessage.error('Failed to load booking policies')
  } finally {
    loading.value = false
  }
}

const submitForm = async () => {
  if (!formRef.value) return
  
  await formRef.value.validate(async (valid) => {
    if (!valid) return
    
    saving.value = true
    try {
      const res = await updateBookingPolicies(policyForm)
      if (res.success) {
        ElMessage.success('Booking policies updated successfully')
        await fetchPolicies()
      } else {
        ElMessage.error(res.message || 'Failed to update booking policies')
      }
    } catch (error) {
      console.error('Failed to update booking policies:', error)
      ElMessage.error(error.response?.data?.message || 'Failed to update booking policies')
    } finally {
      saving.value = false
    }
  })
}

const resetForm = () => {
  if (!formRef.value) return
  formRef.value.resetFields()
  fetchPolicies()
}

onMounted(() => {
  fetchPolicies()
})
</script>

<style scoped lang="scss">
.booking-policy {
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
  
  .policy-card {
    background: var(--bg-color-white, #ffffff);
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    transition: background-color 0.3s ease;
    
    @media (max-width: 768px) {
      border-radius: 10px;
    }
    
    @media (max-width: 480px) {
      border-radius: 8px;
    }
    
    html.dark & {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
    }
  }
  
  .policy-form {
    padding: 24px;
    
    @media (max-width: 768px) {
      padding: 20px;
    }
    
    @media (max-width: 480px) {
      padding: 16px;
    }
    
    :deep(.el-form-item) {
      margin-bottom: 24px;
      
      @media (max-width: 768px) {
        margin-bottom: 20px;
      }
      
      @media (max-width: 480px) {
        margin-bottom: 16px;
      }
    }
    
    :deep(.el-form-item__label) {
      font-weight: 600;
      font-size: 15px;
      color: var(--color-text-primary, #303133);
      margin-bottom: 8px;
      padding-bottom: 0;
      line-height: 1.5;
      
      @media (max-width: 768px) {
        font-size: 14px;
        margin-bottom: 6px;
      }
      
      @media (max-width: 480px) {
        font-size: 13px;
        margin-bottom: 5px;
      }
      
      html.dark & {
        color: var(--color-text-regular, #e2e8f0);
      }
    }
    
    :deep(.el-textarea__inner) {
      border-radius: 8px;
      border: 1px solid var(--border-color-light, #dcdfe6);
      font-size: 14px;
      line-height: 1.6;
      padding: 12px;
      min-height: 80px;
      transition: all 0.3s ease;
      
      @media (max-width: 768px) {
        font-size: 14px;
        padding: 10px;
        min-height: 70px;
      }
      
      @media (max-width: 480px) {
        font-size: 13px;
        padding: 8px;
        min-height: 60px;
        line-height: 1.5;
      }
      
      &:focus {
        border-color: var(--el-color-primary, #409eff);
      }
      
      html.dark & {
        background-color: var(--bg-color-base, #2d3748);
        border-color: var(--border-color-light, #4a5568);
        color: var(--color-text-primary, #f7fafc);
        
        &:focus {
          border-color: var(--el-color-primary, #409eff);
        }
      }
    }
    
    :deep(.el-input__count) {
      background-color: transparent;
      color: var(--color-text-secondary, #909399);
      font-size: 12px;
      bottom: 8px;
      right: 10px;
      
      html.dark & {
        color: var(--color-text-secondary, #a0aec0);
      }
      
      @media (max-width: 768px) {
        font-size: 11px;
        bottom: 6px;
        right: 8px;
      }
      
      @media (max-width: 480px) {
        font-size: 10px;
        bottom: 5px;
        right: 6px;
      }
    }
    
    .action-buttons {
      display: flex;
      gap: 12px;
      padding-top: 8px;
      
      @media (max-width: 768px) {
        gap: 10px;
      }
      
      @media (max-width: 480px) {
        flex-direction: column;
        gap: 8px;
      }
      
      .el-button {
        flex: 1;
        min-width: 120px;
        height: 44px;
        font-size: 15px;
        font-weight: 500;
        border-radius: 8px;
        
        @media (max-width: 768px) {
          height: 42px;
          font-size: 14px;
        }
        
        @media (max-width: 480px) {
          width: 100%;
          height: 40px;
          font-size: 14px;
        }
      }
    }
  }
}
</style>
